// Wrappers for the BMD student-side runtime endpoints, all under the
// `/studentBmd/*` route set in tutor-backend. The backend side is
// `routes/StudentBmdRuntimeRoutes.js`, then
// `controllers/StudentBmdRuntimeController.js`, then
// `models/StudentBmdRuntimeModel.js`.
//
// Use the shared `apiClient` from `src/infra/api-client.ts`, not raw axios:
// its interceptors handle Bearer-token injection and the expired-token
// redirect, and calling axios directly would duplicate that auth logic.

import { apiClient } from "../../../infra/api-client"
import { BASE_URL } from "../../../global"
import type {
  GetDQRequest,
  GetDQResponse,
  GetBmdSessionStateRequest,
  GetBmdSessionStateResponse,
  SubmitBmdAnswerRequest,
  SubmitBmdAnswerResponse,
  SkipBmdQuestionRequest,
  SkipBmdQuestionResponse,
  CompleteBmdTopicRequest,
  CompleteBmdTopicResponse,
} from "../types/bmd.types"

// Grouped so a change to the backend route namespace is one find and replace.
const ENDPOINTS = {
  getDQ: "studentBmd/getDQ",
  getSessionState: "studentBmd/getSessionState",
  submitAnswer: "studentBmd/submitAnswer",
  skipQuestion: "studentBmd/skipQuestion",
  completeTopic: "studentBmd/completeTopic",
} as const

// =============================================================================
// Service functions
// =============================================================================

// Fetches one DQ by id. The backend decides what to return based on `mode`
// (and `pk_session_question_key` entitlement): in test mode, `answer_is_correct`
// and `misconception_type` are stripped to prevent network-inspection cheating.
export async function getDQ(
  req: GetDQRequest,
  signal?: AbortSignal,
): Promise<GetDQResponse> {
  const response = await apiClient.post<GetDQResponse>(
    BASE_URL + ENDPOINTS.getDQ,
    {
      diagnostic_id: req.diagnostic_id,
      pk_session_question_key: req.pk_session_question_key,
      mode: req.mode ?? "test",
    },
    { signal },
  )
  return response.data
}

export async function getBmdSessionState(
  req: GetBmdSessionStateRequest,
  signal?: AbortSignal,
): Promise<GetBmdSessionStateResponse> {
  const response = await apiClient.post<GetBmdSessionStateResponse>(
    BASE_URL + ENDPOINTS.getSessionState,
    { diagnostics_session_key: req.diagnostics_session_key },
    { signal },
  )
  return response.data
}

export async function submitBmdAnswer(
  req: SubmitBmdAnswerRequest,
): Promise<SubmitBmdAnswerResponse> {
  const response = await apiClient.post<SubmitBmdAnswerResponse>(
    BASE_URL + ENDPOINTS.submitAnswer,
    req,
  )
  return response.data
}

export async function skipBmdQuestion(
  req: SkipBmdQuestionRequest,
): Promise<SkipBmdQuestionResponse> {
  const response = await apiClient.post<SkipBmdQuestionResponse>(
    BASE_URL + ENDPOINTS.skipQuestion,
    req,
  )
  return response.data
}

export async function completeBmdTopic(
  req: CompleteBmdTopicRequest,
): Promise<CompleteBmdTopicResponse> {
  const response = await apiClient.post<CompleteBmdTopicResponse>(
    BASE_URL + ENDPOINTS.completeTopic,
    req,
  )
  return response.data
}
